import { useClientConversations } from "@/features/messages/hooks/conversationQueries";
import { usePhoneNumber } from "@/features/messages/hooks/phoneNumberQueries";
import { CustomerPaymentMethods } from "@/features/payment-methods/components/CustomerPaymentMethods";
import { ApiError } from "@/core/api/apiTypes";
import { Button } from "@/shared/components/shadcn/Button";
import { CompactActions } from "@/shared/components/ui/CompactActions";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { InfoRow } from "@/shared/components/ui/InfoRow";
import { ItemList } from "@/shared/components/ui/ItemList";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { PageHeader, PageHeaderAction } from "@/shared/components/ui/PageHeader";
import { RowSkeleton } from "@/shared/components/ui/RowSkeleton";
import { Section } from "@/shared/components/ui/Section";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import { LucideCalendar, LucideCheck, LucideCopy, LucideMail, LucideMessageSquare, LucidePhone } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { ClientAppointmentHistory } from "../components/ClientAppointmentHistory";
import { ClientNotes } from "../components/ClientNotes";
import { useClient } from "../hooks/clientQueries";
import type { Client } from "../types/clientTypes";
import { getClientName } from "../utils/clientDisplay";

export function ClientDetailsPage() {
  const { clientId } = useParams<{ clientId: string }>();
  const { goToClientEdit, goToClients } = useAppNavigation();
  const { data: client, isLoading, error, refetch } = useClient(clientId || "");
  const notFound = error instanceof ApiError && error.Status === 404;

  return (
    <Page>
      <PageHeader
        title={client ? getClientName(client) : "Customer details"}
        actions={client ? <PageHeaderAction aria-label="Edit customer" onClick={() => goToClientEdit(client.id)}>Edit</PageHeaderAction> : undefined}
      />
      <PageContent>
        {client ? (
          <>
            {error && <ErrorState message={error.message} onRetry={() => void refetch()} />}
            <ClientDetailsContent key={client.id} client={client} />
          </>
        ) : isLoading ? <RowSkeleton /> : error && !notFound ? (
          <ErrorState message={error.message} onRetry={() => void refetch()} />
        ) : (
          <EmptyState message="Customer not found" action={{ label: "View customers", onClick: goToClients }} />
        )}
      </PageContent>
    </Page>
  );
}

function ClientDetailsContent({ client }: { client: Client }) {
  const { goToClientAppointmentAdd, goToConversation } = useAppNavigation();
  const phoneQuery = usePhoneNumber();
  const conversationsQuery = useClientConversations(client.id, phoneQuery.data?.id);
  const conversation = conversationsQuery.data?.find((item) => item.clientId === client.id);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(copyTimer.current), []);

  const handleCopy = async (text: string, field: string) => {
    clearTimeout(copyTimer.current);
    setCopiedField(null);
    setCopyError(null);
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable");
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      copyTimer.current = setTimeout(() => setCopiedField(null), 2000);
    } catch {
      setCopyError(`Couldn't copy ${field.toLowerCase()}. Select and copy it from Contact details, or try Copy again.`);
    }
  };
  const copyAction = (text: string | null, field: string) => (
    <Button variant="quiet" disabled={!text} aria-label={`Copy ${field.toLowerCase()}`} onClick={() => { if (text) void handleCopy(text, field); }}>
      {copiedField === field ? <LucideCheck aria-hidden="true" /> : <LucideCopy aria-hidden="true" />}
      <span className="sr-only sm:not-sr-only">{copiedField === field ? "Copied" : "Copy"}</span>
    </Button>
  );
  const messagingError = phoneQuery.error || conversationsQuery.error;

  return (
    <div className="space-y-[26px]">
      <div className="min-w-0 space-y-3">
        <CompactActions>
          <Button onClick={() => goToClientAppointmentAdd(client.id)}><LucideCalendar aria-hidden="true" />Book</Button>
          <Button variant="outline" disabled={!conversation || !!messagingError} onClick={() => { if (conversation) goToConversation(conversation.id); }}>
            <LucideMessageSquare aria-hidden="true" />Message
          </Button>
        </CompactActions>
        {messagingError && <ErrorState message={messagingError.message} onRetry={() => { void phoneQuery.refetch(); if (phoneQuery.data) void conversationsQuery.refetch(); }} />}
              </div>
      <div className="grid items-start gap-[26px] md:grid-cols-2">
        <Section title="Contact details">
          <ItemList
            items={[
              { id: "email", field: "Email", value: client.email, empty: "No email added", icon: LucideMail },
              { id: "phone", field: "Phone", value: client.phoneNumber, empty: "No phone added", icon: LucidePhone },
            ]}
            renderItem={({ field, value, empty, icon: Icon }) => (
              <InfoRow title={value || empty} topCaption={field} density="compact" icon={<Icon className="size-5 text-muted-foreground" aria-hidden="true" />} actions={copyAction(value, field)} />
            )}
          />
          <p role="status" className="sr-only">{copiedField ? `${copiedField} copied.` : ""}</p>
          {copyError && <ErrorState message={copyError} />}
        </Section>
        <ClientNotes client={client} />
      </div>
        <CustomerPaymentMethods clientId={client.id} />
        <ClientAppointmentHistory clientId={client.id} />
    </div>
  );
}
